// frontend/src/components/SuperAdmin.js
import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Spinner, Modal, Form, Button, Badge } from 'react-bootstrap';
import api from '../api';

const PLAN_COLORS = {
  trial:      '#f4a261',
  starter:    '#457b9d',
  pro:        '#2a9d8f',
  enterprise: '#1d3557',
};

const PLAN_LABELS = {
  trial: 'Essai', starter: 'Starter', pro: 'Pro', enterprise: 'Enterprise'
};

function StatCard({ icon, label, value, color }) {
  return (
    <div style={{
      background: 'white', borderRadius: '14px', padding: '20px 24px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)', borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ fontSize: '24px', marginBottom: '8px' }}>{icon}</div>
      <div style={{ fontSize: '28px', fontWeight: '800', color: '#1a1a2e' }}>{value}</div>
      <div style={{ fontSize: '12px', color: color, fontWeight: '600',
        textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>{label}</div>
    </div>
  );
}

function SuperAdmin() {
  const [stats, setStats]           = useState(null);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [showModal, setShowModal]   = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [editModal, setEditModal]   = useState(null);
  const [form, setForm]             = useState({
    entreprise_nom: '', username: '', password: '', email: '', plan: 'trial'
  });
  const [editForm, setEditForm]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch]         = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, entRes] = await Promise.all([
        api.get('/users/admin/stats'),
        api.get('/users/admin/entreprises'),
      ]);
      setStats(statsRes.data);
      setEntreprises(entRes.data);
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/users/admin/entreprises', form);
      setShowModal(false);
      setForm({ entreprise_nom: '', username: '', password: '', email: '', plan: 'trial' });
      await fetchData();
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (id, actif) => {
    try {
      await api.put(`/users/admin/entreprises/${id}/toggle`);
      await fetchData();
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put(`/users/admin/entreprises/${editModal.id}`, editForm);
      setEditModal(null);
      await fetchData();
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (e) => {
    setEditModal(e);
    setEditForm({
      nom: e.nom, plan: e.plan, actif: e.actif,
      email_contact: e.email_contact || '',
    });
  };

  const filtered = entreprises.filter(e =>
    e.nom.toLowerCase().includes(search.toLowerCase()) ||
    (e.email_contact || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="text-center mt-5"><Spinner animation="border" /></div>
  );

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {/* Bannière super admin */}
      <div style={{
        background: 'linear-gradient(135deg, #1d3557 0%, #457b9d 100%)',
        borderRadius: '16px', padding: '24px 28px', marginBottom: '28px',
        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: '22px', fontWeight: '800', marginBottom: '4px' }}>
            🛡️ Back-office Super Admin
          </div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>
            Gestion globale de toutes les entreprises clientes
          </div>
        </div>
        <button onClick={() => setShowModal(true)} style={{
          background: 'white', color: '#1d3557', border: 'none',
          borderRadius: '10px', padding: '10px 20px', fontWeight: '700',
          fontSize: '14px', cursor: 'pointer',
        }}>
          + Nouvelle entreprise
        </button>
      </div>

      {/* KPIs */}
      {stats && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '14px', marginBottom: '28px',
        }}>
          <StatCard icon="🏢" label="Entreprises" value={stats.nb_entreprises} color="#1d3557" />
          <StatCard icon="✅" label="Actives" value={stats.nb_actives} color="#2a9d8f" />
          <StatCard icon="⏳" label="En essai" value={stats.nb_trial} color="#f4a261" />
          <StatCard icon="👥" label="Utilisateurs" value={stats.nb_users} color="#457b9d" />
          <StatCard icon="🏗️" label="Chantiers" value={stats.nb_chantiers} color="#6d6875" />
          <StatCard icon="💰" label="CA total" value={`${(stats.ca_total || 0).toLocaleString('fr-FR')} €`} color="#e9c46a" />
        </div>
      )}

      {/* Barre recherche */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h5 style={{ margin: 0, fontWeight: '700', color: '#1a1a2e' }}>
          Entreprises ({filtered.length})
        </h5>
        <input
          type="text" placeholder="Rechercher..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{
            padding: '8px 14px', borderRadius: '10px', border: '1px solid #e0e0e0',
            fontSize: '14px', width: '250px', outline: 'none',
          }}
        />
      </div>

      {/* Liste entreprises */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {filtered.map(e => {
          const trialEnd = e.trial_ends_at ? new Date(e.trial_ends_at) : null;
          const trialDays = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / (1000*60*60*24))) : null;
          const isExpired = trialDays !== null && trialDays === 0;

          return (
            <div key={e.id} style={{
              background: 'white', borderRadius: '12px', padding: '16px 20px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              borderLeft: `4px solid ${e.actif ? PLAN_COLORS[e.plan] : '#ddd'}`,
              opacity: e.actif ? 1 : 0.6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {/* Infos principales */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <strong style={{ fontSize: '15px', color: '#1a1a2e' }}>{e.nom}</strong>
                    <span style={{
                      background: PLAN_COLORS[e.plan] + '20', color: PLAN_COLORS[e.plan],
                      borderRadius: '6px', padding: '1px 8px', fontSize: '11px', fontWeight: '700',
                    }}>{PLAN_LABELS[e.plan]}</span>
                    {!e.actif && <span style={{
                      background: '#fee2e2', color: '#e63946',
                      borderRadius: '6px', padding: '1px 8px', fontSize: '11px', fontWeight: '700',
                    }}>DÉSACTIVÉE</span>}
                    {isExpired && <span style={{
                      background: '#fee2e2', color: '#e63946',
                      borderRadius: '6px', padding: '1px 8px', fontSize: '11px', fontWeight: '700',
                    }}>EXPIRÉ</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {e.email_contact || '—'} · {e.ville || '—'}
                    · Créée le {e.created_at ? new Date(e.created_at).toLocaleDateString('fr-FR') : '—'}
                  </div>
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', gap: '20px', flexShrink: 0 }}>
                  {[
                    { label: 'Users', value: e.nb_users },
                    { label: 'Chantiers', value: e.nb_chantiers },
                    { label: 'Factures', value: e.nb_factures },
                    { label: 'CA', value: `${(e.ca_total || 0).toLocaleString('fr-FR')} €` },
                  ].map(s => (
                    <div key={s.label} style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: '700', fontSize: '16px', color: '#1a1a2e' }}>{s.value}</div>
                      <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase' }}>{s.label}</div>
                    </div>
                  ))}
                  {trialDays !== null && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: '700', fontSize: '16px', color: isExpired ? '#e63946' : '#f4a261' }}>
                        {trialDays}j
                      </div>
                      <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase' }}>Essai</div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button onClick={() => setShowDetail(e)} style={{
                    background: '#f0f0f0', border: 'none', borderRadius: '8px',
                    padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                  }}>👁️ Détail</button>
                  <button onClick={() => openEdit(e)} style={{
                    background: '#e8f4fd', color: '#457b9d', border: 'none', borderRadius: '8px',
                    padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                  }}>✏️ Modifier</button>
                  <button onClick={() => handleToggle(e.id, e.actif)} style={{
                    background: e.actif ? '#fee2e2' : '#dcfce7',
                    color: e.actif ? '#e63946' : '#16a34a',
                    border: 'none', borderRadius: '8px',
                    padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                  }}>{e.actif ? '🔒 Désactiver' : '🔓 Activer'}</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal créer entreprise */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>🏢 Nouvelle entreprise cliente</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreate}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nom de l'entreprise *</Form.Label>
              <Form.Control type="text" required value={form.entreprise_nom}
                onChange={e => setForm({...form, entreprise_nom: e.target.value})} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Plan</Form.Label>
              <Form.Select value={form.plan} onChange={e => setForm({...form, plan: e.target.value})}>
                <option value="trial">Essai (14 jours)</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </Form.Select>
            </Form.Group>
            <hr />
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
              Compte administrateur de l'entreprise :
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Username *</Form.Label>
              <Form.Control type="text" required value={form.username}
                onChange={e => setForm({...form, username: e.target.value})} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control type="email" required value={form.email}
                onChange={e => setForm({...form, email: e.target.value})} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Mot de passe *</Form.Label>
              <Form.Control type="password" required value={form.password}
                onChange={e => setForm({...form, password: e.target.value})} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Création...' : 'Créer l\'entreprise'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal modifier entreprise */}
      <Modal show={!!editModal} onHide={() => setEditModal(null)}>
        <Modal.Header closeButton>
          <Modal.Title>✏️ Modifier — {editModal?.nom}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEdit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nom</Form.Label>
              <Form.Control type="text" value={editForm.nom || ''}
                onChange={e => setEditForm({...editForm, nom: e.target.value})} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Plan</Form.Label>
              <Form.Select value={editForm.plan || 'trial'}
                onChange={e => setEditForm({...editForm, plan: e.target.value})}>
                <option value="trial">Essai</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email contact</Form.Label>
              <Form.Control type="email" value={editForm.email_contact || ''}
                onChange={e => setEditForm({...editForm, email_contact: e.target.value})} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Date fin d'essai</Form.Label>
              <Form.Control type="datetime-local"
                value={editForm.trial_ends_at ? editForm.trial_ends_at.substring(0,16) : ''}
                onChange={e => setEditForm({...editForm, trial_ends_at: e.target.value})} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setEditModal(null)}>Annuler</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal détail entreprise */}
      <Modal show={!!showDetail} onHide={() => setShowDetail(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>👁️ {showDetail?.nom}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showDetail && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                {[
                  { label: 'Email', value: showDetail.email_contact },
                  { label: 'Ville', value: showDetail.ville },
                  { label: 'Plan', value: PLAN_LABELS[showDetail.plan] },
                  { label: 'Statut', value: showDetail.actif ? '✅ Active' : '❌ Désactivée' },
                  { label: 'Chantiers', value: showDetail.nb_chantiers },
                  { label: 'Factures', value: showDetail.nb_factures },
                  { label: 'CA total', value: `${(showDetail.ca_total || 0).toLocaleString('fr-FR')} €` },
                  { label: 'Créée le', value: showDetail.created_at ? new Date(showDetail.created_at).toLocaleDateString('fr-FR') : '—' },
                ].map(item => (
                  <div key={item.label} style={{ background: '#f8f9fa', borderRadius: '8px', padding: '10px 14px' }}>
                    <div style={{ fontSize: '11px', color: '#999', textTransform: 'uppercase', marginBottom: '2px' }}>{item.label}</div>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>{item.value || '—'}</div>
                  </div>
                ))}
              </div>
              <h6 style={{ fontWeight: '700', marginBottom: '10px' }}>Utilisateurs ({showDetail.nb_users})</h6>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {(showDetail.users || []).map(u => (
                  <div key={u.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#f8f9fa', borderRadius: '8px', padding: '8px 14px',
                  }}>
                    <div>
                      <strong>{u.username}</strong>
                      <span style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}>{u.email}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <Badge bg={u.role === 'admin' ? 'danger' : u.role === 'chef_chantier' ? 'warning' : 'secondary'}>
                        {u.role}
                      </Badge>
                      <Badge bg={u.active ? 'success' : 'secondary'}>
                        {u.active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetail(null)}>Fermer</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SuperAdmin;
